// Legal Widget — niche list. Table on desktop, card grid on mobile.
//
// Lists every niche (slug, displayName, version, partnerCount, pageCount) from
// GET /api/v1/admin/niches. Each row links to the editor + observability; the
// header button routes to the clone flow. Ported from the legal-widget
// dashboard's NicheList screen onto admin-web's theme primitives + legalApi.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { legalApi, type NicheSummary } from "../../lib/legalApi";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Skeleton,
  tokens,
} from "../../lib/theme";

export default function LegalNiches() {
  const [items, setItems] = useState<NicheSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    legalApi
      .listNiches()
      .then((d) => {
        if (!cancelled) {
          setItems(d);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title="Niches"
        subtitle="Each niche is a full, isolated legal / consent content set."
        actions={
          <Link to="/legal/new" style={{ textDecoration: "none" }}>
            <button type="button" style={buttonStyle("primary")}>
              + New niche
            </button>
          </Link>
        }
      />

      {loading && !items && (
        <Card>
          <Skeleton height={48} />
          <div style={{ height: 12 }} />
          <Skeleton height={48} />
          <div style={{ height: 12 }} />
          <Skeleton height={48} />
        </Card>
      )}

      {error && !items && (
        <Card>
          <EmptyState title="Failed to load niches" hint={error} />
        </Card>
      )}

      {items && items.length === 0 && (
        <Card>
          <EmptyState
            title="No niches yet"
            hint="Clone an existing niche to create your first content set."
          />
        </Card>
      )}

      {items && items.length > 0 && (
        <Card padding={0}>
          <ResponsiveTable items={items} />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResponsiveTable: real <table> on desktop, stacked cards on mobile
// ---------------------------------------------------------------------------

function ResponsiveTable({ items }: { items: NicheSummary[] }) {
  return (
    <>
      {/* Desktop table */}
      <div data-table-desktop style={{ display: "none" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: tokens.font.size.base,
          }}
        >
          <thead>
            <tr>
              <Th>Niche</Th>
              <Th>Version</Th>
              <Th align="right">Partners</Th>
              <Th align="right">Pages</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((n, idx) => (
              <tr
                key={n.slug}
                style={{
                  background:
                    idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                }}
              >
                <Td>
                  <Link
                    to={`/legal/${encodeURIComponent(n.slug)}`}
                    style={{
                      color: tokens.color.fg0,
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    {n.displayName}
                  </Link>
                  <div
                    style={{
                      color: tokens.color.fg2,
                      fontSize: tokens.font.size.xs,
                      fontFamily: tokens.font.mono,
                      marginTop: 2,
                    }}
                  >
                    {n.slug}
                  </div>
                </Td>
                <Td>
                  <Pill tone="neutral">v{n.version}</Pill>
                </Td>
                <Td align="right" mono>
                  {n.partnerCount}
                </Td>
                <Td align="right" mono>
                  {n.pageCount}
                </Td>
                <Td align="right">
                  <span style={{ display: "inline-flex", gap: tokens.space.md }}>
                    <Link to={`/legal/${encodeURIComponent(n.slug)}`}>Edit</Link>
                    <Link
                      to={`/legal/${encodeURIComponent(n.slug)}/observability`}
                    >
                      Observability
                    </Link>
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div
        data-table-mobile
        style={{
          display: "grid",
          gap: tokens.space.md,
          padding: tokens.space.md,
        }}
      >
        {items.map((n) => (
          <article
            key={n.slug}
            style={{
              background: tokens.color.bg3,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: tokens.radius.md,
              padding: tokens.space.md,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: tokens.space.sm,
                marginBottom: tokens.space.sm,
              }}
            >
              <div>
                <Link
                  to={`/legal/${encodeURIComponent(n.slug)}`}
                  style={{
                    color: tokens.color.fg0,
                    fontWeight: 600,
                    fontSize: tokens.font.size.lg,
                    textDecoration: "none",
                  }}
                >
                  {n.displayName}
                </Link>
                <div
                  style={{
                    color: tokens.color.fg2,
                    fontSize: tokens.font.size.xs,
                    fontFamily: tokens.font.mono,
                    marginTop: 2,
                  }}
                >
                  {n.slug}
                </div>
              </div>
              <Pill tone="neutral">v{n.version}</Pill>
            </div>
            <dl
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: tokens.space.sm,
                margin: `0 0 ${tokens.space.md}px`,
              }}
            >
              <Stat label="Partners" value={String(n.partnerCount)} />
              <Stat label="Pages" value={String(n.pageCount)} />
            </dl>
            <div style={{ display: "flex", gap: tokens.space.md }}>
              <Link to={`/legal/${encodeURIComponent(n.slug)}`}>Edit</Link>
              <Link to={`/legal/${encodeURIComponent(n.slug)}/observability`}>
                Observability
              </Link>
            </div>
          </article>
        ))}
      </div>

      <ResponsiveCSS />
    </>
  );
}

function ResponsiveCSS() {
  useEffect(() => {
    const id = "loop-rtb-legal-niches-responsive";
    if (document.getElementById(id)) return;
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = `
      @media (min-width: ${tokens.breakpoint.md}px) {
        [data-table-desktop] { display: block !important; }
        [data-table-mobile] { display: none !important; }
      }
    `;
    document.head.appendChild(tag);
  }, []);
  return null;
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg1,
        fontSize: tokens.font.size.xs,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg0,
        ...(mono
          ? {
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
            }
          : {}),
      }}
    >
      {children}
    </td>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg2,
          marginBottom: 2,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          color: tokens.color.fg0,
          fontFamily: tokens.font.mono,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </dd>
    </div>
  );
}
